import { auth } from '../firebase';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Get franchisee registration and compliance status report
 * @returns {Promise<{data: Array, summary: Object|null}>} Report rows and summary metadata
 * @throws {Error} Throws an error if the fetch fails or the user is not authenticated
 */
export const getFranchiseeRegistrationReport = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/reports/franchisee-registration-status`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return {
      data: responseData.data || [],
      summary: responseData.summary || null
    };

  } catch (error) {
    console.error("Error fetching franchisee registration report:", error);
    throw error;
  }
};

/**
 * Get report of completed jobs without invoices and/or RCTIs
 * @param {Object} options - Filter options
 * @param {string} options.startDate - Filter jobs completed on or after this date (ISO string)
 * @param {string} options.endDate - Filter jobs completed on or before this date (ISO string)
 * @param {string} options.missingType - Filter by what's missing: 'invoice', 'rcti', 'both', 'any' (default: 'any')
 * @returns {Promise<Object>} Report data with data array and summary
 * @throws {Error} Throws an error if the fetch fails or the user is not authenticated
 */
/**
 * Download the Franchisee Compliance Register PDF.
 * Triggers a browser file download.
 * @throws {Error} if user is not authenticated or the request fails
 */
export const downloadComplianceRegisterPDF = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();
  const url = `${BASE_URL}/reports/compliance-register-pdf`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${idToken}` },
  });

  if (!response.ok) {
    // Try to parse error JSON
    let message = `HTTP error! status: ${response.status}`;
    try {
      const data = await response.json();
      message = data.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }

  // Trigger browser download
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  const today = new Date().toISOString().split('T')[0];
  a.download = `Franchisee_Compliance_Register_${today}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
};

export const getJobsWithoutInvoicesReport = async (options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();

    // Build query string from options
    const queryParams = new URLSearchParams();
    if (options.startDate) queryParams.append('startDate', options.startDate);
    if (options.endDate) queryParams.append('endDate', options.endDate);
    if (options.missingType) queryParams.append('missingType', options.missingType);

    const queryString = queryParams.toString();
    const url = `${BASE_URL}/reports/jobs-without-invoices${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return {
      data: responseData.data || [],
      summary: responseData.summary || {
        totalCompleted: 0,
        totalMissing: 0,
        missingInvoice: 0,
        missingRcti: 0,
        missingBoth: 0
      }
    };

  } catch (error) {
    console.error("Error fetching jobs without invoices report:", error);
    throw error;
  }
};

/**
 * Get list of suppliers (franchisees) for the report dropdown
 * @returns {Promise<Array>} List of supplier objects
 */
export const getSuppliersList = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/reports/suppliers`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData.data || [];

  } catch (error) {
    console.error("Error fetching suppliers list:", error);
    throw error;
  }
};

/**
 * Get weekly client report for a selected supplier
 * @param {string} supplierId - The franchisee ID
 * @returns {Promise<Object>} Report data with supplier info, summary, and clients grouped with jobs
 */
export const getWeeklyClientReport = async (supplierId, weekStartDate = null) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    let url = `${BASE_URL}/reports/weekly-client-report?supplierId=${encodeURIComponent(supplierId)}`;
    if (weekStartDate) {
      url += `&weekStartDate=${encodeURIComponent(weekStartDate)}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData.data;

  } catch (error) {
    console.error("Error fetching weekly client report:", error);
    throw error;
  }
};

// Reads Content-Disposition's filename= if present, else falls back.
const filenameFromDisposition = (response, fallback) => {
  const header = response.headers.get('Content-Disposition') || '';
  const match = header.match(/filename="?([^";]+)"?/i);
  return match ? match[1] : fallback;
};

/**
 * Download the Weekly Client Report PDF for a single supplier.
 * Triggers a browser file download. Never sends any email.
 * @param {string} supplierId
 * @param {string} [weekStartDate] - YYYY-MM-DD (Monday); server defaults if omitted
 * @param {string} [fallbackFileName] - used if the server doesn't send Content-Disposition
 */
export const downloadWeeklyClientReportPDF = async (supplierId, weekStartDate = null, fallbackFileName = 'Weekly_Client_Report.pdf') => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();
  const params = new URLSearchParams({ supplierId });
  if (weekStartDate) params.set('weekStartDate', weekStartDate);
  const url = `${BASE_URL}/reports/weekly-client-report-pdf?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${idToken}` },
  });

  if (!response.ok) {
    let message = `HTTP error! status: ${response.status}`;
    try {
      const data = await response.json();
      message = data.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filenameFromDisposition(response, fallbackFileName);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
};

/**
 * Generate (but never send) the weekly client report manifest for every supplier.
 * Populates the "All Suppliers" table and the confirmation modal — no email goes out.
 * @param {string} [weekStartDate] - YYYY-MM-DD (Monday)
 * @returns {Promise<{weekStart:string, weekEnd:string, suppliers:Array}>}
 */
export const generateAllWeeklyClientReports = async (weekStartDate = null) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();
  const url = `${BASE_URL}/reports/weekly-client-report/generate-all`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(weekStartDate ? { weekStartDate } : {}),
  });

  const responseData = await response.json().catch(() => ({}));

  if (!response.ok || responseData.status !== 'success') {
    throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
  }

  return responseData.data;
};

/**
 * Email the weekly client report PDF to a single supplier.
 * The server resolves the recipient from clients/{supplierId}.email — this call never carries an address.
 * @param {Object} params
 * @param {string} params.supplierId
 * @param {string} [params.weekStartDate]
 * @param {string} [params.expectedEmail] - the address the admin was shown; server 409s if it no longer matches
 * @returns {Promise<{supplierId:string, supplierName:string, sentTo:string, logId?:string}>}
 */
export const emailWeeklyClientReport = async ({ supplierId, weekStartDate = null, expectedEmail = null }) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();
  const url = `${BASE_URL}/reports/weekly-client-report/email`;

  const body = { supplierId };
  if (weekStartDate) body.weekStartDate = weekStartDate;
  if (expectedEmail) body.expectedEmail = expectedEmail;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const responseData = await response.json().catch(() => ({}));

  if (!response.ok || responseData.status !== 'success') {
    const error = new Error(responseData.message || `HTTP error! status: ${response.status}`);
    // Surface the documented failure modes (422 no-email, 409 email changed since confirm, 404 unknown supplier)
    error.code = responseData.code
      || (response.status === 422 ? 'no-email' : response.status === 409 ? 'email-changed' : response.status === 404 ? 'not-found' : 'error');
    error.httpStatus = response.status;
    throw error;
  }

  return responseData.data;
};

/**
 * Get completed jobs per franchisee with configurable filters
 * @param {Object} params - { days?: number, statuses?: string, overdueBuckets?: string }
 * @returns {Promise<Object>} Report data with summary and franchisees array with jobs
 */
export const getCompletedJobsPerFranchisee = async (params = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const queryParams = new URLSearchParams();
    if (params.days) queryParams.set('days', params.days);
    if (params.statuses) queryParams.set('statuses', params.statuses);
    if (params.overdueBuckets) queryParams.set('overdueBuckets', params.overdueBuckets);
    if (params.franchiseeId) queryParams.set('franchiseeId', params.franchiseeId);

    const queryString = queryParams.toString();
    const url = `${BASE_URL}/reports/completed-jobs-per-franchisee${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData.data;

  } catch (error) {
    console.error("Error fetching completed jobs per franchisee report:", error);
    throw error;
  }
};
